import { MeshV2 } from './meshV2.types'
import { MeshStyles, ContentArea } from '../Types'
import { getLeft, getMarginLeft, getMarginRight, getJustify, unitize, DEFAULT_UNIT_SIZE } from './meshCssUtils'
import { Margins, UnitSize, MeshItemLayout, SingleLayoutData } from '@wix/thunderbolt-becky-types'

type CompMeshLayout = {
	compId: string
	span: number
	startsAtRow: number
	endsAtRow: number
	margins: Margins
	left: UnitSize
	right?: UnitSize | undefined
	// wedge styles
	isWedge?: true
	height?: UnitSize
}

type GridLines = {
	[lineId: number]: {
		startingComps: Array<CompMeshLayout>
		endingComps: Array<CompMeshLayout>
	}
}

const getCompLayoutForMesh = (compId: string, itemLayout: MeshItemLayout): CompMeshLayout => ({
	compId,
	span: itemLayout.gridArea.rowEnd - itemLayout.gridArea.rowStart,
	startsAtRow: itemLayout.gridArea.rowStart,
	endsAtRow: itemLayout.gridArea.rowEnd,
	margins: itemLayout.margins,
	left: itemLayout.left,
	right: itemLayout.right,
})

const getWedgeLayoutForMesh = (compId: string, containerId: string, itemLayout: MeshItemLayout): CompMeshLayout => ({
	compId: `${containerId}-wedge-${compId}`,
	isWedge: true,
	span: itemLayout.gridArea.rowStart - itemLayout.minDistanceFromRow!.startsAtRow || 1,
	startsAtRow: itemLayout.minDistanceFromRow!.startsAtRow,
	endsAtRow: itemLayout.gridArea.rowStart,
	margins: {
		top: DEFAULT_UNIT_SIZE,
		bottom: DEFAULT_UNIT_SIZE,
	},
	height: itemLayout.minDistanceFromRow!.distance,
	left: DEFAULT_UNIT_SIZE,
})

const getGridLinesMap = (allComps: Array<CompMeshLayout>) => {
	const gridLines: GridLines = {}

	const getLineAtIndex = (lineId: number) => {
		if (!gridLines[lineId]) {
			gridLines[lineId] = { startingComps: [], endingComps: [] }
		}
		return gridLines[lineId]
	}

	allComps.forEach((comp) => {
		getLineAtIndex(comp.startsAtRow).startingComps.push(comp)
		getLineAtIndex(comp.endsAtRow).endingComps.push(comp)
	})

	return gridLines
}

const getCssStyles = ({
	containerId,
	containerMinHeight,
	lastRow,
	allComps,
	contentArea,
}: {
	containerId: string
	containerMinHeight: number | undefined
	lastRow: number
	allComps: Array<CompMeshLayout>
	contentArea?: ContentArea
}): MeshStyles => {
	const gridRowsWithMinContent = lastRow > 2 ? lastRow - 2 : 0

	const cssStyles: MeshStyles = {
		[containerId]: {
			height: 'auto',
		},
		[`${containerId}-gridContainer`]: {
			position: 'static',
			display: 'grid',
			height: 'auto',
			width: '100%',
			minHeight: containerMinHeight ? `${containerMinHeight}px` : 'auto',
			gridTemplateRows: gridRowsWithMinContent ? `repeat(${gridRowsWithMinContent}, min-content) 1fr` : '1fr',
		},
	}
	allComps.forEach((comp) => {
		cssStyles[comp.compId] = {
			position: 'relative',
			gridArea: `${comp.startsAtRow} / 1 / ${comp.endsAtRow} / 2`,
			left: getLeft(comp),
			// @ts-ignore TODO: fix
			margin: `${unitize(comp.margins.top!)} ${getMarginRight(comp)} ${unitize(
				// @ts-ignore TODO: fix
				comp.margins.bottom!
			)} ${getMarginLeft(comp, contentArea)}`,
			justifySelf: getJustify(comp),
			alignSelf: 'start',
			...(comp.isWedge && { height: `${unitize(comp.height!)}`, visibility: 'hidden', width: '0' }),
		}
	})

	return cssStyles
}

const getStructure = (containerId: string, allComps: Array<CompMeshLayout>) => ({
	id: containerId,
	children: [
		{
			id: `${containerId}-gridWrapper`,
			children: [
				{
					id: `${containerId}-gridContainer`,
					children: allComps.map((comp) => ({ id: comp.compId, children: [] })),
				},
			],
		},
	],
})

export const meshV2 = ({
	id: containerId = 'root',
	childIdToSingleLayoutData = {},
	height: containerMinHeight,
	contentArea,
}: {
	id?: string
	childIdToSingleLayoutData?: { [compId: string]: SingleLayoutData }
	height?: number
	contentArea?: ContentArea
}): MeshV2 => {
	// all comps and wedges from mesh
	const allComps = Object.entries(childIdToSingleLayoutData).reduce((acc, [compId, meshItemLayout]) => {
		const { itemLayout } = meshItemLayout
		if (!itemLayout) {
			return acc
		}
		const compMeshLayout: CompMeshLayout = getCompLayoutForMesh(compId, itemLayout as MeshItemLayout)
		acc.push(compMeshLayout)

		// add wedge
		// TODO: check we don't have a wedge that starts and ends at the same row
		// if we do, we should choose the one with the biggest distance
		if (itemLayout?.type === 'MeshItemLayout' && itemLayout.minDistanceFromRow) {
			const wedge: CompMeshLayout = getWedgeLayoutForMesh(compId, containerId, itemLayout)
			acc.push(wedge)
		}

		return acc
	}, [] as Array<CompMeshLayout>)

	// fill gridLines map
	const gridLines: GridLines = getGridLinesMap(allComps)

	const sortedLineIds = Object.keys(gridLines)
		.map((lineId) => Number(lineId))
		.sort((a, b) => a - b)

	// set rowStart and rowEnd for each comp respecting extra rows
	let currentRow = 0
	sortedLineIds.forEach((lineId) => {
		currentRow++ // TODO: deal with 1000 rows limitation

		const line = gridLines[lineId]

		// sort ending comps by span and wedge
		const sortedEndingComps = line.endingComps.sort(
			(a, b) => a.span + (a.isWedge ? 1 : 0) - b.span - (b.isWedge ? 1 : 0)
		)
		sortedEndingComps.forEach((comp, index) => {
			// component that spanning on several rows or wedge must end at its own row (aka extra row)
			const shouldAddExtraRow = index !== 0 && (comp.span !== 1 || comp.isWedge === true)
			if (shouldAddExtraRow) {
				currentRow++ // TODO: deal with 1000 rows limitation
			}
			comp.endsAtRow = currentRow
		})

		line.startingComps.forEach((comp) => {
			comp.startsAtRow = currentRow
		})
	})

	// generate css and structure
	const styles: MeshStyles = getCssStyles({
		containerId,
		containerMinHeight,
		lastRow: currentRow,
		allComps,
		contentArea,
	})
	const structure = getStructure(containerId, allComps)

	return {
		styles,
		structure,
	}
}
